import * as React from "react"

export enum CreateSiteType {
  Provision = "provision",
  Import = "import",
}

export enum ProvisionConfigurationStep {
  BasicConfiguration = 1,
  ConnectIntegrations = 2,
  ConfigurationComplete = 3,
}

export enum SiteConfigurationStep {
  BasicConfiguration = 1,
  CmsIntegration = 2,
  EnvironmentVariables = 3,
}

// This hook handles keeping track of site id during the creation of a site
// and ensures that it is persisted across page refreshes by utilizing sessionStorage
const CREATE_SITE_STATE_STORAGE_KEY = "createSiteState"

function useCreateSiteState(createSiteType: CreateSiteType) {
  const [step, setStep] = React.useState(
    createSiteType === CreateSiteType.Provision
      ? ProvisionConfigurationStep.BasicConfiguration
      : SiteConfigurationStep.BasicConfiguration
  )
  const [siteId, setSiteId] = React.useState<string | null>(null)

  // initialize state from sessionStorage
  React.useEffect(() => {
    const persistedSiteState = sessionStorage.getItem(
      CREATE_SITE_STATE_STORAGE_KEY
    )
    if (persistedSiteState) {
      const parsed = JSON.parse(persistedSiteState)
      if (parsed.createSiteType === createSiteType) {
        setStep(parsed.step)
        setSiteId(parsed.siteId)
      } else {
        clearSiteState()
      }
    }

    return () => clearSiteState()
  }, [])

  // persist step and siteId when they change
  React.useEffect(() => {
    sessionStorage.setItem(
      CREATE_SITE_STATE_STORAGE_KEY,
      JSON.stringify({ step, siteId, createSiteType })
    )
  }, [step, siteId])

  // little helper for cleaning the slate
  function clearSiteState() {
    sessionStorage.removeItem(CREATE_SITE_STATE_STORAGE_KEY)
  }

  return { step, setStep, siteId, setSiteId } as const
}

export default useCreateSiteState
